import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormArray, FormControl, FormGroup, UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { NewDialogComponent } from "common/newdialog/dialog.component";
import { ActivatedRoute } from "@angular/router";
import { AiService } from "common/ai-content/ai.service";
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { Observable, Subject } from "rxjs";
import { map, takeUntil, tap } from "rxjs/operators";
import { ViewService } from "core/view.service";
import { Sequence } from "process/element/sequence.interface";
import { MutexService } from "common/concurrency/mutex.service";
import { EditService } from "process/element/edit/edit.service";
import { MainService } from "core/main.service";
import { maxValidator } from "common/form/max.directive";
import { HttpErrorResponse } from "@angular/common/http";
import { SelectionInput, SelectionOption } from "common/form/input/types/selection-input";
import ProcessTreeItem = stages.process.ProcessTreeItem;
import Prompt = stages.common.Prompt;
import SequenceLevel = stages.contentgenerator.SequenceLevel;
import ProcessView = stages.process.ProcessView;

interface GenerateNameError {
	type: "FORMATTING_ERROR" | "PROMPTLOADING_ERROR" | "SYSTEM_ERROR";
	messageKey: string;
	errorResponseMessage?: string;
}

@Component({
	selector: "stages-process-creator",
	templateUrl: "./process-creator.component.html",
	styleUrls: ["./process-creator.component.scss"],
})
export class ProcessCreatorComponent implements OnInit, OnDestroy {
	@ViewChild("dialog")
	dialog!: NewDialogComponent;

	private destroy$ = new Subject<boolean>();

	private workspaceId: string = "";
	private type: string = "";
	private pv: string = "";
	private identity: string = "";

	private SELECTIONINPUTKEY: string = "aiTemplates";
	private RESULTS_ARRAY_NAME = "resultsArray";
	protected MAX_NAME_LENGTH = "255";

	// Form
	protected form = new UntypedFormGroup({});
	protected selectionInput?: SelectionInput;
	protected selectionOptions: SelectionOption[] = [];
	protected isContentCreatorConfigured$: Observable<boolean>;
	protected updateInProgress = false;
	protected saveInProgress: boolean = false;
	protected generateNamesError?: GenerateNameError;
	private self?: ProcessView;
	protected readonly Object = Object;

	sequence$: Observable<Sequence>;

	private level: SequenceLevel = "CHILD";

	constructor(
		private activatedRoute: ActivatedRoute,
		private formBuilder: UntypedFormBuilder,
		private aiService: AiService,
		private viewService: ViewService,
		private mutexService: MutexService,
		private editService: EditService,
		private mainService: MainService,
	) {
		this.copyParamMap();
		this.form = this.setupTemplateSelectorForm();

		this.isContentCreatorConfigured$ = this.mainService.applicationContext$.pipe(
			map((applicationState) => applicationState.textCreatorConfigured),
		);

		this.viewService
			.awaitSelfElementObservable()
			.pipe(
				tap((view) => {
					this.self = view;
					const prompts = view.promptContainer?.prompts.filter((p) => p.promptType === "NAMES") ?? [];
					if (prompts.length === 0) {
						this.generateNamesError = {
							type: "PROMPTLOADING_ERROR",
							messageKey: "ai.widget.error.promptNotAvailable",
						};
					} else if (this.selectionInput) {
						this.updateTemplateSelectorOptions(prompts, this.selectionInput, this.form);
					}
				}),
				takeUntil(this.destroy$),
			)
			.subscribe();

		this.sequence$ = this.activatedRoute.data.pipe(
			map((data) => {
				this.level = data.sequence.level;
				switch (data.sequence.level) {
					case "same": {
						this.level = "SAME";
						break;
					}
					case "child": {
						this.level = "CHILD";
						break;
					}
					default: {
						this.level = "CHILD";
						break;
					}
				}

				return data.sequence;
			}),
		);
	}

	ngOnInit(): void {
		this.form.addControl(this.RESULTS_ARRAY_NAME, new FormArray<FormControl>([]));
	}

	protected async generateElementNames(addOneMore: boolean): Promise<void> {
		this.updateInProgress = true;
		this.generateNamesError = undefined;

		await this.aiService
			.getChildElementNamesList(
				this.workspaceId,
				this.type,
				this.pv,
				this.identity,
				this.form.controls[this.SELECTIONINPUTKEY].value[0],
				this.collectElementNames(),
				this.level,
				addOneMore ? 1 : undefined,
			)
			.then((result) => {
				if ((result[0] && result[0] === "") || result.length === 0) {
					this.generateNamesError = {
						type: "FORMATTING_ERROR",
						messageKey: "ai.process.creator.widget.resultError",
					};
				} else {
					for (const item of result) {
						this.addListEntry(item);
					}
				}
			})
			.catch((errorResponse: unknown) => {
				if (errorResponse instanceof HttpErrorResponse) {
					this.generateNamesError = {
						type: "SYSTEM_ERROR",
						messageKey: "ai.widget.error.general",
						errorResponseMessage: errorResponse.error.message,
					};
				} else {
					throw errorResponse;
				}
			});
		this.updateInProgress = false;
	}

	private collectElementNames(): string[] {
		return this.getGeneratedResults(this.form).controls.map((result) => result.value);
	}

	protected save(sequence: Sequence): void {
		const self = this.self;
		if (this.form.valid && self !== undefined) {
			this.saveInProgress = true;

			const origin = sequence.level === "same" ? self.parent : self;
			const originViewable = origin as stages.process.ViewableElement;
			const predecessor =
				sequence.level === "same" && self.type.ident === sequence.targetType ? (self as ProcessTreeItem) : null;
			const predecessorViewable = predecessor as stages.process.ViewableElement;

			this.mutexService.invoke("mutexAddSequence", async () => {
				await this.editService.addSequence(
					originViewable,
					predecessorViewable,
					this.collectElementNames(),
					sequence.associationType,
					sequence.targetType,
					sequence.targetSubtype,
					sequence.sourceRole,
					this.workspaceId,
					this.pv,
				);
				await this.viewService.refreshView(this.workspaceId, this.pv);
				this.close();
			});
		}
	}

	close(): void {
		this.dialog?.close();
	}

	ngOnDestroy(): void {
		this.destroy$.next(true);
		this.destroy$.complete();
	}

	// Setup methods
	private copyParamMap(): void {
		this.workspaceId = this.activatedRoute.snapshot.paramMap.get("workspaceId") ?? "no_workspaceId_in_paramMap";
		this.type = this.activatedRoute.snapshot.paramMap.get("type") ?? "no_type_in_paramMap";
		this.pv = this.activatedRoute.snapshot.paramMap.get("processVersion") ?? "no_pv_in_paramMap";
		this.identity = this.activatedRoute.snapshot.paramMap.get("identity") ?? "no_identity_in_paramMap";
	}

	private setupTemplateSelectorForm(): FormGroup {
		this.selectionInput = new SelectionInput({
			key: this.SELECTIONINPUTKEY,
			multiple: false,
			selectionOptions: [],
			translateKey: "ai.process.creator.widget.templateselection",
		});

		return this.formBuilder.group({
			aiTemplates: this.formBuilder.control(this.selectionInput.value, this.selectionInput),
		});
	}

	private updateTemplateSelectorOptions(
		prompts: Prompt[],
		selectionInput: SelectionInput,
		form: UntypedFormGroup,
	): UntypedFormGroup {
		for (const prompt of prompts) {
			this.selectionOptions.push({
				value: prompt.id,
				untranslatableName: prompt.name,
			});
		}
		selectionInput.selectionOptions = this.selectionOptions;
		selectionInput.setValueToFirstOption();
		form.controls[selectionInput.key].setValue(selectionInput.value);
		return form;
	}

	// Methods to modify list
	protected addListEntry(generatedResult: string): void {
		this.getGeneratedResults(this.form).push(
			new FormControl(generatedResult, [maxValidator(255), Validators.required, Validators.pattern(/^[^|]+$/)]),
		);
	}

	protected deleteListEntry(index: number): void {
		this.getGeneratedResults(this.form).removeAt(index);
	}

	protected sortList(event: CdkDragDrop<string[]>): void {
		moveItemInArray(this.getGeneratedResults(this.form).controls, event.previousIndex, event.currentIndex);
	}

	protected getGeneratedResults(form: UntypedFormGroup): FormArray<FormControl> {
		return form.get(this.RESULTS_ARRAY_NAME) as FormArray;
	}

	protected getMaxInputLength(index: string): string {
		const resultsArray = this.getGeneratedResults(this.form);
		let actualLength: string = "0";
		const control = resultsArray.get(index);
		if (control?.value) {
			actualLength = control.value.length.toString();
		}
		return actualLength.toString() + "/" + this.MAX_NAME_LENGTH.toString();
	}
}
